import React from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingCart, User, ShieldAlert, ScrollText, ExternalLink, AlertTriangle } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPE_META = {
  listing: { icon: Package, label: "Listing", accent: "border-l-olive-bright" },
  order: { icon: ShoppingCart, label: "Order", accent: "border-l-volt" },
  account: { icon: User, label: "Account", accent: "border-l-teal-400" },
  report: { icon: ShieldAlert, label: "Report", accent: "border-l-blaze" },
  audit_event: { icon: ScrollText, label: "Audit", accent: "border-l-olive-bright" },
};

export function StaffContextCard({ card }) {
  if (!card) return null;
  const meta = TYPE_META[card.type] || { icon: ScrollText, label: card.type, accent: "border-l-sniper" };
  const Icon = meta.icon;
  return (
    <div className={`mt-2 border border-sniper ${meta.accent} border-l-2 bg-obsidian/60 p-2.5 max-w-md`} data-testid={`context-card-${card.type}-${card.id}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" strokeWidth={1.8} />
        <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{meta.label}</span>
        <span className="font-mono text-[9px] text-muted-foreground/60 truncate">{card.id}</span>
      </div>
      {card.missing ? (
        <p className="mt-1 text-xs font-mono text-blaze flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Object not found (may have been removed).</p>
      ) : (
        <div className="mt-1.5">
          {card.type === "listing" && (
            <div className="flex items-center gap-2">
              {card.photo && <img src={card.photo} alt="" className="w-9 h-9 object-cover border border-sniper shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm font-body text-foreground truncate">{card.title || "(untitled)"}</p>
                <p className="text-[11px] font-mono text-muted-foreground">{money((card.price || 0) * 100)} · {card.status}</p>
              </div>
            </div>
          )}
          {card.type === "order" && (
            <div>
              <p className="text-sm font-body text-foreground truncate">{card.title || "Order"}</p>
              <p className="text-[11px] font-mono text-muted-foreground">
                @{card.buyer_username} → @{card.seller_username} · {money(card.amount_cents)}
              </p>
              <p className="text-[11px] font-mono mt-0.5">
                <span className={card.disputed ? "text-blaze" : "text-muted-foreground"}>{card.status}</span>
                {card.chargeback_open && <span className="text-blaze font-bold"> · CHARGEBACK</span>}
              </p>
            </div>
          )}
          {card.type === "account" && (
            <div>
              <p className="text-sm font-body text-foreground truncate">@{card.username} <span className="text-[10px] font-mono uppercase text-muted-foreground">· {card.role}</span></p>
              <p className="text-[11px] font-mono text-muted-foreground">
                {card.verified ? "verified" : "unverified"}
                {card.warnings > 0 && <span className="text-volt"> · {card.warnings} warning(s)</span>}
                {card.banned && <span className="text-blaze"> · SUSPENDED</span>}
              </p>
            </div>
          )}
          {card.type === "report" && (
            <div>
              <p className="text-sm font-body text-foreground truncate">{card.category || "Uncategorized"} · {card.status}</p>
              <p className="text-[11px] font-mono text-muted-foreground truncate">
                {card.reported_username ? `on @${card.reported_username}` : ""} {card.reason ? `— ${card.reason}` : ""}
              </p>
            </div>
          )}
          {card.type === "audit_event" && (
            <div>
              <p className="text-sm font-body text-foreground truncate">{card.summary || card.action}</p>
              <p className="text-[11px] font-mono text-muted-foreground">{card.action} · {card.actor}</p>
            </div>
          )}
          {card.link && (
            <Link to={card.link} className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono uppercase text-olive-bright hover:text-foreground" data-testid={`context-card-link-${card.id}`}>
              Open <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default StaffContextCard;
